import { useState, useEffect, useRef, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Input, Tabs, Spin, Empty, message } from 'antd'
import { SearchOutlined } from '@ant-design/icons'
import { useDebounce } from '../../hooks/useTheme'
import { assetApi, dataSourcesApi } from '../../services/api'
import { useAuth } from '../../contexts/AuthContext'

const TAB_ALL = 'all'
const TAB_DATA = 'data'
const TAB_ASSETS = 'assets'

type TabKey = typeof TAB_ALL | typeof TAB_DATA | typeof TAB_ASSETS

interface DataItem {
  key: string
  id: string
  name: string
  type?: string
  value?: number
  [key: string]: unknown
}

interface AssetItem {
  key: string
  id: string
  name: string
  type?: string
  category?: string
  value?: number
  [key: string]: unknown
}

function normalizeDataItems(res: any): DataItem[] {
  const raw = res?.data
  const list = Array.isArray(raw?.items)
    ? raw.items
    : Array.isArray(raw?.list)
      ? raw.list
      : Array.isArray(raw)
        ? raw
        : []
  return list.slice(0, 6).map((item: any, i: number) => ({
    key: String(item.key ?? item.id ?? i + 1),
    id: item.id ?? item.data_id ?? '',
    name: item.name ?? item.data_name ?? '-',
    type: item.type,
    value: Number(item.value ?? item.amount ?? 0),
  }))
}

function normalizeAssetItems(res: any): AssetItem[] {
  const raw = res?.data
  const list = Array.isArray(raw?.items)
    ? raw.items
    : Array.isArray(raw?.list)
      ? raw.list
      : Array.isArray(raw)
        ? raw
        : []
  return list.slice(0, 6).map((item: any, i: number) => ({
    key: String(item.key ?? item.id ?? i + 1),
    id: item.id ?? '',
    name: item.name ?? '-',
    type: item.type,
    category: item.category,
    value: Number(item.value ?? 0),
  }))
}

function highlightKeyword(text: string, keyword: string) {
  if (!keyword.trim()) return text
  const k = keyword.trim().replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  const re = new RegExp(`(${k})`, 'gi')
  return text.replace(re, '<span class="text-orange-500 font-medium">$1</span>')
}

export default function HeaderSearch() {
  const navigate = useNavigate()
  const { hasPermission } = useAuth()
  const canAccessData = hasPermission('data_sources:list') || hasPermission('data_sources:summary')
  const canAccessAssets = hasPermission('asset_products:list') || hasPermission('asset_products:detail')
  const [visible, setVisible] = useState(false)
  const [keyword, setKeyword] = useState('')
  const [activeTab, setActiveTab] = useState<TabKey>(TAB_ALL)
  const [dataItems, setDataItems] = useState<DataItem[]>([])
  const [assetItems, setAssetItems] = useState<AssetItem[]>([])
  const [loading, setLoading] = useState(false)
  const [dataTotal, setDataTotal] = useState(0)
  const [assetTotal, setAssetTotal] = useState(0)
  const wrapperRef = useRef<HTMLDivElement>(null)

  const debouncedKeyword = useDebounce(keyword.trim(), 300)

  useEffect(() => {
    if (!debouncedKeyword) {
      setDataItems([])
      setAssetItems([])
      setDataTotal(0)
      setAssetTotal(0)
      return
    }
    const needData = activeTab === TAB_ALL || activeTab === TAB_DATA
    const needAssets = activeTab === TAB_ALL || activeTab === TAB_ASSETS
    setLoading(true)
    const promises: Promise<void>[] = []
    if (needData) {
      promises.push(
        dataSourcesApi
          .list({
            page: 1,
            pageSize: 6,
            filters: { keyword: debouncedKeyword } as Record<string, string>,
          })
          .then((res: any) => {
            const list = normalizeDataItems(res)
            const total = Number(res?.data?.total ?? res?.data?.totalCount ?? list.length)
            setDataItems(list)
            setDataTotal(total)
          })
          .catch(() => {
            setDataItems([])
            setDataTotal(0)
          })
      )
    }
    if (needAssets) {
      promises.push(
        assetApi
          .list({
            page: 1,
            pageSize: 6,
            filters: { keyword: debouncedKeyword },
          })
          .then((res: any) => {
            const list = normalizeAssetItems(res)
            const total = Number(res?.data?.total ?? res?.data?.totalCount ?? list.length)
            setAssetItems(list)
            setAssetTotal(total)
          })
          .catch(() => {
            setAssetItems([])
            setAssetTotal(0)
          })
      )
    }
    if (!needData) setDataItems([])
    if (!needAssets) setAssetItems([])
    Promise.all(promises).finally(() => setLoading(false))
  }, [debouncedKeyword, activeTab])

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setVisible(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleGoData = (item?: DataItem) => {
    if (!canAccessData) {
      message.warning('暂无资产数据访问权限')
      return
    }
    setVisible(false)
    navigate('/data', {
      state: item
        ? { searchKeyword: debouncedKeyword, highlightId: item.id }
        : { searchKeyword: debouncedKeyword },
    })
  }

  const handleGoAssets = (item?: AssetItem) => {
    if (!canAccessAssets) {
      message.warning('暂无资产产品访问权限')
      return
    }
    setVisible(false)
    navigate('/assets', {
      state: item
        ? { searchKeyword: debouncedKeyword, highlightId: item.id }
        : { searchKeyword: debouncedKeyword },
    })
  }

  const tabItems = useMemo(() => {
    const items: { key: TabKey; label: string }[] = []
    if (canAccessData || canAccessAssets) items.push({ key: TAB_ALL, label: '全部' })
    if (canAccessData) items.push({ key: TAB_DATA, label: '资产数据' })
    if (canAccessAssets) items.push({ key: TAB_ASSETS, label: '资产产品' })
    return items
  }, [canAccessData, canAccessAssets])

  const showPanel = visible

  return (
    <div ref={wrapperRef} className="relative" style={{ width: 320 }}>
      <Input
        placeholder="搜索资产数据、资产产品..."
        prefix={<SearchOutlined className="text-gray-400" />}
        value={keyword}
        onChange={(e) => setKeyword(e.target.value)}
        onFocus={() => setVisible(true)}
        onPressEnter={() => {
          if (!debouncedKeyword) return
          if (activeTab === TAB_DATA) handleGoData()
          else if (activeTab === TAB_ASSETS) handleGoAssets()
          else if (canAccessData) handleGoData()
          else if (canAccessAssets) handleGoAssets()
        }}
        allowClear
        style={{ width: '100%' }}
      />
      {showPanel && (
        <div
          className="absolute top-full left-0 mt-1 z-50 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden"
          style={{ width: 480, maxHeight: 420 }}
        >
          {tabItems.length > 0 && (
            <Tabs
              activeKey={tabItems.some((t) => t.key === activeTab) ? activeTab : tabItems[0]?.key ?? TAB_ALL}
              onChange={(k) => setActiveTab(k as TabKey)}
              size="small"
              className="px-2 pt-2"
              items={tabItems}
            />
          )}
          <div className="px-3 pb-3 overflow-y-auto" style={{ maxHeight: 360 }}>
            {loading ? (
              <div className="flex justify-center py-8">
                <Spin tip="搜索中..." />
              </div>
            ) : !debouncedKeyword ? (
              <div className="py-6 text-center text-gray-500 dark:text-gray-400 text-sm">
                输入关键词搜索资产数据、资产产品
              </div>
            ) : tabItems.length === 0 ? (
              <div className="py-6 text-center text-gray-500 dark:text-gray-400 text-sm">
                暂无数据或资产访问权限
              </div>
            ) : (
              <>
                {(activeTab === TAB_ALL || activeTab === TAB_DATA) && canAccessData && (
                  <div className="mb-4">
                    <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                      资产数据 {dataTotal > 0 ? `· 共 ${dataTotal} 条` : ''}
                    </div>
                    {dataItems.length === 0 ? (
                      <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无结果" className="py-2" />
                    ) : (
                      <ul className="space-y-1">
                        {dataItems.map((item) => (
                          <li
                            key={item.key}
                            className="px-2 py-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer text-sm"
                            onClick={() => handleGoData(item)}
                          >
                            <span
                              dangerouslySetInnerHTML={{
                                __html: highlightKeyword(item.name, debouncedKeyword),
                              }}
                            />
                            {item.type && (
                              <span className="ml-2 text-gray-400 dark:text-gray-500 text-xs">{item.type}</span>
                            )}
                          </li>
                        ))}
                        {dataTotal > dataItems.length && (
                          <li
                            className="px-2 py-1 text-blue-600 dark:text-blue-400 text-sm cursor-pointer"
                            onClick={() => handleGoData()}
                          >
                            查看更多资产数据 →
                          </li>
                        )}
                      </ul>
                    )}
                  </div>
                )}
                {(activeTab === TAB_ALL || activeTab === TAB_ASSETS) && canAccessAssets && (
                  <div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                      资产产品 {assetTotal > 0 ? `· 共 ${assetTotal} 条` : ''}
                    </div>
                    {assetItems.length === 0 ? (
                      <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无结果" className="py-2" />
                    ) : (
                      <ul className="space-y-1">
                        {assetItems.map((item) => (
                          <li
                            key={item.key}
                            className="px-2 py-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer text-sm"
                            onClick={() => handleGoAssets(item)}
                          >
                            <span
                              dangerouslySetInnerHTML={{
                                __html: highlightKeyword(item.name, debouncedKeyword),
                              }}
                            />
                            {(item.type || item.category) && (
                              <span className="ml-2 text-gray-400 dark:text-gray-500 text-xs">
                                {[item.type, item.category].filter(Boolean).join(' · ')}
                              </span>
                            )}
                          </li>
                        ))}
                        {assetTotal > assetItems.length && (
                          <li
                            className="px-2 py-1 text-blue-600 dark:text-blue-400 text-sm cursor-pointer"
                            onClick={() => handleGoAssets()}
                          >
                            查看更多资产产品 →
                          </li>
                        )}
                      </ul>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
